import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

const DEFAULT_LOCATION_SUGGESTIONS = [
  'Clínica',
  'Online',
  'Domiciliar',
  'Escola',
  'Sala de atendimento',
]

function normalizeLocation(value: string | null | undefined): string | null {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

async function fetchLocationSuggestions(
  organizationId: string
): Promise<string[]> {
  const [sessionsResult, schedulesResult] = await Promise.all([
    supabase
      .from('sessions')
      .select('location_text')
      .eq('organization_id', organizationId)
      .not('location_text', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(100),
    supabase
      .from('recurring_schedules')
      .select('location_text')
      .eq('organization_id', organizationId)
      .not('location_text', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(100),
  ])

  if (sessionsResult.error) throw sessionsResult.error
  if (schedulesResult.error) throw schedulesResult.error

  const usedLocations = [
    ...(sessionsResult.data ?? []),
    ...(schedulesResult.data ?? []),
  ]
    .map((row) => normalizeLocation(row.location_text))
    .filter((value): value is string => value !== null)

  const uniqueLocations = new Map<string, string>()
  for (const location of [...usedLocations, ...DEFAULT_LOCATION_SUGGESTIONS]) {
    const key = location.toLocaleLowerCase('pt-BR')
    if (!uniqueLocations.has(key)) {
      uniqueLocations.set(key, location)
    }
  }

  return Array.from(uniqueLocations.values()).slice(0, 20)
}

export function useAppointmentLocationSuggestions() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  const query = useQuery({
    queryKey: ['appointment-location-suggestions', organizationId],
    queryFn: () => fetchLocationSuggestions(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  })

  return useMemo(() => query.data ?? DEFAULT_LOCATION_SUGGESTIONS, [query.data])
}
